import { useState, type FormEvent } from "react";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { FormField } from "@/components/ui/FormField";
import { Input } from "@/components/ui/Input";
import { ApiError } from "@/lib/api";

import {
  useAddresses,
  useCreateAddress,
  useDeleteAddress,
  useUpdateAddress,
  type AccountAddress
} from "./api";

interface AddressFormState {
  id?: number;
  label: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
  default_delivery: boolean;
}

const BLANK: AddressFormState = {
  label: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  postal_code: "",
  default_delivery: false
};

function toFormState(a: AccountAddress): AddressFormState {
  return {
    id: a.id,
    label: a.label ?? "",
    line1: a.line1,
    line2: a.line2 ?? "",
    city: a.city,
    state: a.state,
    postal_code: a.postal_code,
    default_delivery: a.default_delivery
  };
}

// Phase 2a customer-accounts FR-8 — address book.
export function AddressesTab(): JSX.Element {
  const addresses = useAddresses();
  const create = useCreateAddress();
  const update = useUpdateAddress();
  const del = useDeleteAddress();
  const [editing, setEditing] = useState<AddressFormState | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editing) return;
    setErrors({});
    try {
      const payload = {
        label: editing.label || null,
        line1: editing.line1,
        line2: editing.line2 || null,
        city: editing.city,
        state: editing.state,
        postal_code: editing.postal_code,
        default_delivery: editing.default_delivery
      };
      if (editing.id) {
        await update.mutateAsync({ id: editing.id, ...payload });
      } else {
        await create.mutateAsync(payload);
      }
      setEditing(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        const body = err.body as { errors?: Record<string, string> } | undefined;
        setErrors(body?.errors ?? {});
      }
    }
  };

  const onDelete = async (id: number) => {
    if (!window.confirm("Delete this address?")) return;
    await del.mutateAsync(id);
  };

  if (addresses.isLoading) {
    return (
      <p className="text-sm text-zinc-500" role="status">
        Loading your addresses…
      </p>
    );
  }

  if (addresses.isError) {
    return (
      <Alert variant="destructive" role="alert">
        Couldn&rsquo;t load your addresses.
      </Alert>
    );
  }

  return (
    <div className="space-y-5">
      <ul className="space-y-3">
        {addresses.data?.length === 0 ? (
          <li className="rounded-md border border-dashed border-zinc-300 p-6 text-sm text-zinc-600">
            No addresses saved yet. Add one to speed up checkout.
          </li>
        ) : null}
        {addresses.data?.map((a) => (
          <li
            key={a.id}
            className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-zinc-200 bg-brand-white p-4"
          >
            <div>
              <div className="flex items-center gap-2">
                {a.default_delivery ? (
                  <span aria-label="default delivery" className="text-brand-red">
                    ★
                  </span>
                ) : null}
                <span className="font-medium">{a.label || "Saved address"}</span>
              </div>
              <p className="text-sm text-zinc-700">
                {a.line1}
                {a.line2 ? `, ${a.line2}` : ""}
              </p>
              <p className="text-sm text-zinc-700">
                {a.city}, {a.state} {a.postal_code}
              </p>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setEditing(toFormState(a))}>
                Edit
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => onDelete(a.id)}>
                Delete
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {editing ? (
        <form onSubmit={onSubmit} className="space-y-3 rounded-md border border-zinc-200 bg-brand-white p-4">
          <h2 className="text-sm font-semibold">{editing.id ? "Edit address" : "Add a new address"}</h2>
          <FormField id="label" label="Label" hint="e.g. Main Office, Jobsite" error={errors.label}>
            <Input id="label" value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} />
          </FormField>
          <FormField id="line1" label="Street address" required error={errors.line1}>
            <Input id="line1" value={editing.line1} onChange={(e) => setEditing({ ...editing, line1: e.target.value })} required />
          </FormField>
          <FormField id="line2" label="Unit / Suite (optional)" error={errors.line2}>
            <Input id="line2" value={editing.line2} onChange={(e) => setEditing({ ...editing, line2: e.target.value })} />
          </FormField>
          <div className="grid gap-3 sm:grid-cols-3">
            <FormField id="city" label="City" required error={errors.city}>
              <Input id="city" value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })} required />
            </FormField>
            <FormField id="state" label="State" required error={errors.state}>
              <Input id="state" maxLength={2} value={editing.state} onChange={(e) => setEditing({ ...editing, state: e.target.value })} required />
            </FormField>
            <FormField id="postal_code" label="ZIP" required error={errors.postal_code}>
              <Input id="postal_code" value={editing.postal_code} onChange={(e) => setEditing({ ...editing, postal_code: e.target.value })} required />
            </FormField>
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-700">
            <input
              type="checkbox"
              checked={editing.default_delivery}
              onChange={(e) => setEditing({ ...editing, default_delivery: e.target.checked })}
            />
            Make this my default delivery address
          </label>
          <div className="flex gap-2">
            <Button type="submit" disabled={create.isPending || update.isPending}>
              {editing.id ? "Save changes" : "Add address"}
            </Button>
            <Button type="button" variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <Button type="button" onClick={() => setEditing({ ...BLANK })}>
          + Add address
        </Button>
      )}
    </div>
  );
}
